import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

export interface PlanLimitations {
    plan_id: string
    max_brands: number
    features: Record<string, boolean>
    metadata: Record<string, any>
}

export interface UsageLimits {
    total_brands: number
    feature_usage: Record<string, {
        count: number
        last_used: string
    }>
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000

interface CacheEntry<T> {
    data: T
    timestamp: number
}

// In-memory cache for all active sessions
const limitationsCache: Map<string, CacheEntry<PlanLimitations>> = new Map()
const usageCache: Map<string, CacheEntry<UsageLimits>> = new Map()

export function usePlanLimitations() {
    const [limitations, setLimitations] = useState<PlanLimitations | null>(null)
    const [usage, setUsage] = useState<UsageLimits | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    // Helper to check if cache is valid
    const isCacheValid = useCallback((timestamp: number) => {
        return Date.now() - timestamp < CACHE_DURATION
    }, [])

    // Fetch current plan limitations and usage
    const fetchLimitations = useCallback(async (force = false) => {
        try {
            setLoading(true)
            const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }

            if (!account?.account_id) {
                throw new Error('No account found')
            }

            // Check cache first
            const cachedLimitations = limitationsCache.get(account.account_id)
            const cachedUsage = usageCache.get(account.account_id)

            if (!force && cachedLimitations && cachedUsage &&
                isCacheValid(cachedLimitations.timestamp) &&
                isCacheValid(cachedUsage.timestamp)) {
                setLimitations(cachedLimitations.data)
                setUsage(cachedUsage.data)
                setLoading(false)
                return
            }

            // Get both limitations and usage in parallel
            const [limitationsResult, usageResult] = await Promise.all([
                supabase.rpc('get_plan_limitations'),
                supabase.rpc('get_current_usage')
            ])

            if (limitationsResult.error) throw limitationsResult.error
            if (usageResult.error) throw usageResult.error

            const limitationsData = limitationsResult.data as unknown as PlanLimitations
            const usageData = usageResult.data as unknown as UsageLimits

            // Update cache
            limitationsCache.set(account.account_id, {
                data: limitationsData,
                timestamp: Date.now()
            })
            usageCache.set(account.account_id, {
                data: usageData,
                timestamp: Date.now()
            })

            setLimitations(limitationsData)
            setUsage(usageData)

        } catch (err) {
            setError(err instanceof Error ? err : new Error('Unknown error occurred'))
        } finally {
            setLoading(false)
        }
    }, [isCacheValid])

    // Check if a specific action is allowed
    const isActionAllowed = useCallback(async (
        action: string,
        context: Record<string, any> = {}
    ): Promise<boolean> => {
        try {
            const { data, error } = await supabase.rpc('is_action_allowed', {
                p_action: action,
                p_context: context
            })

            if (error) throw error

            // Force refresh cache after action check
            fetchLimitations(true)

            return !!data

        } catch (err) {
            console.error('Error checking action:', err)
            return false
        }
    }, [fetchLimitations])

    // Get remaining limits from cache
    const getRemainingLimits = useCallback((): {
        remaining_brands: number
    } | null => {
        if (!limitations || !usage) return null

        return {
            remaining_brands: limitations.max_brands - usage.total_brands
        }
    }, [limitations, usage])

    // Check if a feature is available (uses cached data)
    const isFeatureAvailable = useCallback((featureName: string): boolean => {
        if (!limitations) return false
        return !!limitations.features[featureName]
    }, [limitations])

    // Get feature usage stats from cache
    const getFeatureUsage = useCallback((featureName: string): {
        count: number
        last_used: string | null
    } | null => {
        if (!usage?.feature_usage) return null
        const feature = usage.feature_usage[`use_feature_${featureName}`]
        return feature || { count: 0, last_used: null }
    }, [usage])

    // Initial fetch and refresh on interval
    useEffect(() => {
        fetchLimitations()

        // Refresh cache every 4.5 minutes (just before cache expires)
        const interval = setInterval(() => {
            fetchLimitations()
        }, CACHE_DURATION - 30000)

        return () => clearInterval(interval)
    }, [fetchLimitations])

    return {
        limitations,
        usage,
        loading,
        error,
        isActionAllowed,
        getRemainingLimits,
        isFeatureAvailable,
        getFeatureUsage,
        refresh: () => fetchLimitations(true) // Force refresh when explicitly called
    }
} 